import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';
import usePermissionsStore from '../stores/permissionsStore';

function StatCard({ label, value, icon, color = 'green' }) {
  const colors = {
    green: 'border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.95),rgba(209,250,229,0.9))] text-emerald-800',
    blue: 'border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.96),rgba(219,234,254,0.92))] text-sky-800',
    yellow: 'border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(254,243,199,0.92))] text-amber-800',
    purple: 'border-cyan-200 bg-[linear-gradient(135deg,rgba(236,254,255,0.96),rgba(207,250,254,0.92))] text-cyan-800',
  };
  return (
    <div className={`rounded-[24px] border p-4 shadow-sm sm:p-5 ${colors[color]}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/70 text-2xl shadow-sm">{icon}</div>
      </div>
      <div className="text-xl font-bold sm:text-2xl">{value}</div>
      <div className="mt-1 text-sm opacity-80">{label}</div>
    </div>
  );
}

function formatMoney(amount) {
  return new Intl.NumberFormat('sw-TZ').format(amount || 0) + ' TZS';
}

export default function Dashboard() {
  const { T } = useLang();
  const setPermissions = usePermissionsStore(s => s.setPermissions);
  const resetPermissions = usePermissionsStore(s => s.reset);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then((r) => {
      setStats(r.data);
      const dissolveAtEnd = !!+r.data?.group?.dissolve_at_end;
      if (r.data?.member_permissions) {
        setPermissions({ ...r.data.member_permissions, dissolve_at_end: dissolveAtEnd });
      } else {
        setPermissions({ dissolve_at_end: dissolveAtEnd });
      }
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (!stats) return null;

  if (stats.role === 'superadmin') {
    return (
      <div className="space-y-6">
        <DashboardHero
          title={T.dash.title}
          subtitle={T.dash.subtitleSuper}
          badge={T.dash.badgeSuper}
        />

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-4">
          <StatCard icon="👥" label={T.dash.groups} value={stats.group_count} color="green" />
          <StatCard icon="🙋" label={T.dash.members} value={stats.member_count} color="blue" />
          <StatCard icon="📋" label={T.dash.contributions} value={stats.trans_count} color="purple" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <div className="rounded-[24px] border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.96),rgba(209,250,229,0.92))] p-4 sm:p-5">
            <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">{T.dash.monthlyFees}</div>
            <div className="text-2xl font-bold text-green-800">{formatMoney(stats.total_amount)}</div>
            <div className="text-xs text-green-600 mt-1">{T.dash.monthlyFeesDesc}</div>
          </div>
          <div className="rounded-[24px] border border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.96),rgba(219,234,254,0.92))] p-4 sm:p-5">
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">{T.dash.entryFee}</div>
            <div className="text-2xl font-bold text-blue-800">{formatMoney(stats.total_kiingilio)}</div>
            <div className="text-xs text-blue-600 mt-1">{T.dash.entryFeeDesc}</div>
          </div>
          <div className="col-span-2 rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(254,243,199,0.92))] p-4 sm:col-span-1 sm:p-5">
            <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">{T.dash.totalAll}</div>
            <div className="text-2xl font-bold text-yellow-800">{formatMoney(stats.total_all)}</div>
            <div className="text-xs text-yellow-700 mt-1">{T.dash.totalAllDesc}</div>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-3">{T.dash.allGroups}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(stats.groups || []).map((g) => (
              <Link
                key={g.id}
                to={`/groups/${g.id}`}
                className="rounded-[26px] border border-white/75 bg-white/85 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur hover:-translate-y-0.5 transition"
              >
                <h3 className="font-bold text-gray-800">{g.name}</h3>
                <div className="text-sm text-gray-500 mt-2 space-y-1">
                  <div>{T.dash.groupMembers}: <span className="font-medium text-gray-700">{g.member_count}</span></div>
                  <div>{T.dash.groupTotal}: <span className="font-medium text-green-700">{formatMoney(g.total_contributions)}</span></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (stats.role === 'admin') {
    return (
      <div className="space-y-6">
        <DashboardHero
          title={stats.group?.name || T.dash.titleGroup}
          subtitle={T.dash.subtitleAdmin}
          badge={T.dash.badgeAdmin}
        />

        <div className="grid grid-cols-2 gap-3 lg:gap-4">
          <StatCard icon="🙋" label={T.dash.members} value={stats.member_count} color="blue" />
          <StatCard icon="📋" label={T.dash.contributions} value={stats.trans_count} color="purple" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <div className="rounded-[24px] border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.96),rgba(209,250,229,0.92))] p-4 sm:p-5">
            <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">{T.dash.monthlyFees}</div>
            <div className="text-2xl font-bold text-green-800">{formatMoney(stats.total_amount)}</div>
            <div className="text-xs text-green-600 mt-1">{T.dash.monthlyFeesDesc}</div>
          </div>
          <div className="rounded-[24px] border border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.96),rgba(219,234,254,0.92))] p-4 sm:p-5">
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">{T.dash.entryFee}</div>
            <div className="text-2xl font-bold text-blue-800">{formatMoney(stats.total_kiingilio)}</div>
            <div className="text-xs text-blue-600 mt-1">{T.dash.entryFeeDesc}</div>
          </div>
          <div className="col-span-2 rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(254,243,199,0.92))] p-4 sm:col-span-1 sm:p-5">
            <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">{T.dash.totalAll}</div>
            <div className="text-2xl font-bold text-yellow-800">{formatMoney(stats.total_all)}</div>
            <div className="text-xs text-yellow-700 mt-1">{T.dash.totalAllDesc}</div>
          </div>
        </div>

        {(stats.total_transfers > 0 || stats.opening_balance > 0) && (
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-[24px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.96),rgba(254,226,226,0.92))] p-4 sm:p-5">
              <div className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">{T.dash.groupTransfers}</div>
              <div className="text-2xl font-bold text-red-700">− {formatMoney(stats.total_transfers)}</div>
              <div className="text-xs text-red-500 mt-1">{T.dash.groupTransfersDesc}</div>
            </div>
            <div className="rounded-[24px] border border-teal-200 bg-[linear-gradient(135deg,rgba(240,253,250,0.96),rgba(204,251,241,0.92))] p-4 sm:p-5">
              <div className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">{T.dash.netBalance}</div>
              <div className="text-2xl font-bold text-teal-800">{formatMoney(stats.net_balance)}</div>
              <div className="text-xs text-teal-600 mt-1">{T.dash.netBalanceDesc}</div>
            </div>
          </div>
        )}

        <MonthlyChart data={stats.monthly_chart} T={T} />
      </div>
    );
  }

  // Member
  const groupId = stats.member?.group_id;
  const dissolveAtEnd = +stats.group?.dissolve_at_end === 1;

  return (
    <div className="space-y-6">
      <DashboardHero
        title={T.dash.titleMember}
        subtitle={`${T.layout.welcome}, ${stats.member?.name || T.role.member}. ${T.dash.subtitleMember}`}
        badge={T.dash.badgeMember}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <StatCard icon="💰" label={T.dash.myContributions} value={formatMoney(stats.total_amount)} color="green" />
        <StatCard icon="📋" label={T.dash.myCount} value={stats.trans_count} color="blue" />
      </div>

      {/* Group balance — visible to all members, not just admin */}
      {stats.group_net_balance !== undefined && (
        <div className="rounded-[24px] border border-teal-200 bg-[linear-gradient(135deg,rgba(240,253,250,0.96),rgba(204,251,241,0.92))] p-4 sm:p-5">
          <div className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">{T.dash.netBalance}</div>
          <div className="text-2xl font-bold text-teal-800">{formatMoney(stats.group_net_balance)}</div>
          <div className="text-xs text-teal-600 mt-1">{T.dash.netBalanceDesc}</div>
        </div>
      )}

      {/* Fines card — shown only when member has pending fines and hasn't been hidden by admin */}
      {stats.pending_fines > 0 && stats.member_permissions?.fines !== false && groupId && (
        <Link
          to={`/fines?group_id=${groupId}`}
          className="flex items-center justify-between rounded-[26px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.97),rgba(254,226,226,0.93))] px-5 py-4 shadow-sm hover:shadow-md transition-shadow"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-red-600">{T.dash.finesCard}</p>
            <p className="mt-1 text-lg font-bold text-red-800">{formatMoney(stats.pending_fines)}</p>
            <p className="mt-0.5 text-xs text-red-600">{T.dash.finesCardDesc}</p>
          </div>
          <span className="text-3xl">⚠️</span>
        </Link>
      )}

      {/* Distribution card — shown only when group will dissolve */}
      {dissolveAtEnd && groupId && (
        <Link
          to={`/groups/${groupId}/distribution`}
          className="flex items-center justify-between rounded-[26px] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.97),rgba(254,243,199,0.93))] px-5 py-4 shadow-sm hover:shadow-md transition-shadow"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">{T.dash.distCard}</p>
            <p className="mt-1 text-lg font-bold text-amber-800">{T.dash.distCardTitle}</p>
            <p className="mt-0.5 text-xs text-amber-600">{T.dash.distCardDesc}</p>
          </div>
          <span className="text-3xl">💰</span>
        </Link>
      )}

      <MonthlyChart data={stats.monthly_chart} T={T} />

      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">{T.dash.history}</h2>
        <TransactionTable transactions={stats.transactions || []} T={T} />
      </div>
    </div>
  );
}

function DashboardHero({ title, subtitle, badge }) {
  return (
    <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(15,118,110,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <span className="inline-flex rounded-full bg-white/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-50">
            {badge}
          </span>
          <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{title}</h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-emerald-50/80 sm:text-base">{subtitle}</p>
        </div>
        <div className="flex h-16 w-16 items-center justify-center self-start rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
          📈
        </div>
      </div>
    </div>
  );
}

function TransactionTable({ transactions = [], T }) {
  if (!transactions.length) return (
    <div className="rounded-[24px] border border-white/75 bg-white/88 p-6 text-center text-sm text-gray-400">
      {T.dash.noTransactions}
    </div>
  );
  return (
    <div className="rounded-[24px] border border-white/75 bg-white/88 shadow-[0_14px_35px_rgba(15,23,42,0.08)] overflow-hidden">
      <div className="divide-y divide-gray-100">
        {transactions.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-4 py-3 gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{t.member_name || '—'}</p>
              <p className="text-xs text-gray-400">{t.transaction_date?.slice(0, 10)} · {t.type}</p>
            </div>
            <span className="shrink-0 text-sm font-bold text-green-700">
              {new Intl.NumberFormat('sw-TZ').format(t.amount)} TZS
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyChart({ data = [], T }) {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => parseFloat(d.total)));
  return (
    <div className="rounded-[26px] border border-white/75 bg-white/88 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-5">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">{T.dash.monthlyChart}</h2>
      <div className="flex items-end gap-2 h-32">
        {data.map((d) => {
          const height = max > 0 ? (parseFloat(d.total) / max) * 100 : 0;
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-green-500 rounded-t"
                style={{ height: `${height}%`, minHeight: '4px' }}
                title={`${d.month}: ${new Intl.NumberFormat('sw-TZ').format(d.total)}`}
              />
              <span className="text-xs text-gray-400 rotate-45 origin-left truncate w-8">
                {d.month?.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
